import { useCallback, useState } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanelSafe } from '../../../ui/DetailPanel';
import { HaloSpokeHeader } from '../HaloSpokeHeader';
import { TourSpoke } from './TourSpoke';
import { TourSpokeContent } from './TourSpokeContent';

/**
 * Hook that provides handlers to open the tour flow from anywhere.
 *
 * Desktop (with DetailPanel): opens TourSpokeContent in the DetailPanel.
 * Mobile / no DetailPanel: manages a FullscreenOverlay.
 *
 * openTour — starts fresh at SELECT_TOUR_TYPE (for booking a new tour).
 * openTourReschedule — starts at MODIFY_TOUR (shows existing tour + cancel option).
 */
export const useTourAction = (listing?: ListingDetails) => {
  const detailPanel = useDetailPanelSafe();
  const [isOpen, setIsOpen] = useState(false);
  const [isModifyingTour, setIsModifyingTour] = useState(false);

  const useOverlay = !detailPanel || detailPanel.isMobile;

  const openTour = useCallback(() => {
    setIsModifyingTour(false);
    if (detailPanel && !detailPanel.isMobile) {
      detailPanel.openPanel('tour-form', TourSpokeContent, {
        config: { listing, isModifyingTour: false },
        stacked: true,
      });
    } else {
      setIsOpen(true);
    }
  }, [detailPanel, listing]);

  const openTourReschedule = useCallback(() => {
    setIsModifyingTour(true);
    if (detailPanel && !detailPanel.isMobile) {
      detailPanel.openPanel('tour-form', TourSpokeContent, {
        config: { listing, isModifyingTour: true },
        stacked: true,
      });
    } else {
      setIsOpen(true);
    }
  }, [detailPanel, listing]);

  const tourOverlay = useOverlay ? (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <HaloSpokeHeader />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <TourSpoke onClose={() => setIsOpen(false)} listing={listing} isModifyingTour={isModifyingTour} />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  ) : null;

  return { openTour, openTourReschedule, tourOverlay };
};
